import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

// Helper function to get color based on score (0-100)
const getScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
};

// Helper function to get severity color/icon (1-5, assuming higher is worse)
const getSeverityProps = (severity) => {
  switch (severity) {
    case 1:
    case 2:
      return { color: 'success', icon: <CheckCircleOutlineIcon fontSize="small" />, label: 'Low' };
    case 3:
      return { color: 'warning', icon: <WarningAmberIcon fontSize="small" />, label: 'Medium' };
    case 4:
    case 5:
      return { color: 'error', icon: <ErrorOutlineIcon fontSize="small" />, label: 'High' };
    default:
      return { color: 'disabled', icon: <InfoOutlinedIcon fontSize="small" />, label: 'Unknown' };
  }
};

// Helper to format dimension names
const formatDimensionName = (name) => {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const EquityAuditAnalysisTranscript = ({ summaryData }) => {
  const [expandedDimension, setExpandedDimension] = useState(null);
  const [showDataQualityNotes, setShowDataQualityNotes] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedDimension(isExpanded ? panel : false);
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Equity Audit analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;
  const overallScoreColor = getScoreColor(results.overall_equity_score);

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box
        px={2}
        py={1.5}
        bgcolor={`${overallScoreColor}.lighter`}
        borderBottom={1}
        borderColor="divider"
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Equity Audit Analysis{' '}
          <AnalysisLensHelperText lensName="equity_audit" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Score & Summary */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4} md={3} textAlign={{ xs: 'left', sm: 'center' }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Overall Score
            </Typography>
            <Typography
              variant="h3"
              component="div"
              color={`${overallScoreColor}.main`}
              sx={{ fontWeight: 'bold' }}
            >
              {results.overall_equity_score}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={results.overall_equity_score}
              color={overallScoreColor}
              sx={{ height: 8, borderRadius: 4, mt: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Summary
              {results.data_quality_notes && (
                <Tooltip
                  title={
                    showDataQualityNotes ? 'Hide Data Quality Notes' : 'Show Data Quality Notes'
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => setShowDataQualityNotes(!showDataQualityNotes)}
                    sx={{ ml: 0.5, p: 0.2 }}
                  >
                    <InfoOutlinedIcon fontSize="inherit" color="action" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            <Collapse in={showDataQualityNotes}>
              {results.data_quality_notes && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mb: 1,
                    fontStyle: 'italic',
                    borderLeft: 3,
                    borderColor: 'warning.main',
                    pl: 1,
                  }}
                >
                  {results.data_quality_notes}
                </Typography>
              )}
            </Collapse>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {results.summary || 'No summary provided.'}
            </Typography>
          </Grid>
        </Grid>

        {/* Dimensions Accordions */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Dimension Analysis
        </Typography>

        {results.dimensions?.length > 0 ? (
          results.dimensions.map((dim, index) => {
            const panelId = `panel-${index}`;
            const scoreColor = getScoreColor(dim.equity_score);
            const severityProps = getSeverityProps(dim.severity);
            const isExpanded = expandedDimension === panelId;

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1.5,
                  '&:before': {
                    display: 'none', // Remove default top border
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 12px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    bgcolor: isExpanded ? `${scoreColor}.lighter` : 'background.paper',
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: 'divider',
                    borderTopLeftRadius: 'inherit',
                    borderTopRightRadius: 'inherit',
                    minHeight: 64,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap', // Allow wrapping on smaller screens
                      gap: 1, // Add gap between items
                    },
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'medium', flexBasis: { xs: '100%', sm: 'auto' }, mr: 1 }}
                  >
                    {formatDimensionName(dim.dimension)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      flexGrow: 1,
                      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                  >
                    <Tooltip title={`Equity Score: ${dim.equity_score}`}>
                      <Chip
                        label={`${dim.equity_score}`}
                        size="small"
                        color={scoreColor}
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Tooltip>
                    <Tooltip title={`Severity: ${severityProps.label}`}>
                      <Chip
                        icon={severityProps.icon}
                        label={severityProps.label}
                        size="small"
                        color={severityProps.color}
                        variant="outlined"
                      />
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Grid container spacing={3}>
                    {/* Themes */}
                    {dim.themes?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Themes
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {dim.themes.map((theme, idx) => (
                            <Chip key={idx} label={theme} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Gaps/Issues */}
                    {dim.gaps_or_issues?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Gaps / Issues
                        </Typography>
                        <List dense disablePadding>
                          {dim.gaps_or_issues.map((gap, idx) => (
                            <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                <ReportProblemOutlinedIcon fontSize="small" color="warning" />
                              </ListItemIcon>
                              <ListItemText
                                primary={gap}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    {/* Recommendations */}
                    {dim.recommendations?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Recommendations
                        </Typography>
                        <List dense disablePadding>
                          {dim.recommendations.map((rec, idx) => (
                            <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start' }}>
                              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                <LightbulbOutlinedIcon fontSize="small" color="success" />
                              </ListItemIcon>
                              <ListItemText
                                primary={rec}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}

                    {/* Evidence */}
                    {dim.evidence?.length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          Evidence
                        </Typography>
                        <List dense disablePadding>
                          {dim.evidence.map((quote, idx) => (
                            <ListItem
                              key={idx}
                              disableGutters
                              sx={{
                                alignItems: 'flex-start',
                                borderLeft: 2,
                                borderColor: 'grey.300',
                                pl: 1,
                                mb: 1,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                                <FormatQuoteIcon fontSize="small" color="action" />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote.text}
                                secondary={`— ${quote.speaker || 'Unknown'} (Turn ${quote.turn_id ?? 'N/A'})`}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                                secondaryTypographyProps={{
                                  variant: 'caption',
                                  color: 'text.secondary',
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No dimension-specific analysis available.
          </Typography>
        )}
      </CardContent>
      <CardContent sx={{ pt: 0 }}>
        <Typography variant="caption" color="text.secondary">
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default EquityAuditAnalysisTranscript;
